/**
 * Shared session detail panel — renders a session's messages with per-turn
 * traces merged by seq, from tricorder-session's surface via the proxy:
 *  - GET /sessions/{id}           → session
 *  - GET /sessions/{id}/messages  → the transcript
 *  - GET /sessions/{id}/traces    → the per-turn traces
 */

import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { safeFormatDistanceToNow, dateFnsLocale } from '@/utils/dates';
import { cn } from '@/lib/utils';
import type { ApiError } from '@/api/client';
import type { SessionDetail } from '@/hooks/useSessions';
import {
  conversationCitations,
  conversationText,
  type MessageResponse,
  type SessionTraceResponse,
} from '@/api/types_session';
import type { Locale } from 'date-fns';

interface SessionDetailPanelProps {
  session: SessionDetail | undefined;
  isLoading: boolean;
  /** Set when the session fetch failed (e.g. 403 — caller has no access). */
  error?: ApiError | null;
  /** Called by the error state's retry button; button hidden when omitted. */
  onRetry?: () => void;
  /** Per-turn traces (merged onto a turn by its seq). */
  traces: SessionTraceResponse[] | undefined;
  /** True while the traces fetch is in flight — shows a per-message hint. */
  tracesLoading?: boolean;
  /** True when the traces fetch errored — surfaces an inline notice. */
  tracesError?: boolean;
  /** Message ID to highlight (e.g. the selected query or feedbacked message). */
  highlightMessageId?: string | null;
  /** Placeholder text when no session is selected. */
  emptyText?: string;
}

function MessageRow({
  message,
  traces,
  tracesLoading,
  isHighlighted,
  dateLocale,
}: {
  message: MessageResponse;
  traces: SessionTraceResponse[];
  tracesLoading: boolean;
  isHighlighted: boolean;
  dateLocale: Locale;
}) {
  const isUser = message.role === 'user';
  const citations = conversationCitations(message.payload);
  return (
    <div
      className={cn(
        'rounded-lg p-3',
        isHighlighted
          ? 'ring-2 ring-primary/40 bg-primary/5'
          : isUser
            ? 'bg-primary/5 border border-primary/10'
            : 'bg-neutral-50 border border-neutral-200',
      )}
    >
      <div className="flex items-center gap-2 mb-2">
        <Badge
          variant="outline"
          className={cn('text-xs', isUser ? 'border-primary/30' : '')}
        >
          {message.role}
        </Badge>
        <span className="text-xs text-neutral-400">
          {safeFormatDistanceToNow(message.created_at, { addSuffix: true, locale: dateLocale }, '')}
        </span>
      </div>

      <p className="text-sm text-neutral-700 whitespace-pre-wrap">
        {conversationText(message.payload)}
      </p>

      {citations.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {citations.map((cit, i) => (
            <span
              key={i}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs border bg-neutral-50 text-neutral-700 border-neutral-200"
            >
              {cit.document}
              {cit.page_start != null && ` p.${cit.page_start}${cit.page_end != null && cit.page_end !== cit.page_start ? `–${cit.page_end}` : ''}`}
            </span>
          ))}
        </div>
      )}

      {traces.length > 0 ? (
        <div className="mt-3 pt-3 border-t border-neutral-200 flex flex-wrap gap-1">
          {traces.map((trace) => (
            <Badge key={trace.id} variant="outline" className="text-xs">
              {trace.kind}
              {trace.latency_ms != null ? ` · ${trace.latency_ms}ms` : ''}
            </Badge>
          ))}
        </div>
      ) : tracesLoading && !isUser ? (
        <div className="mt-3 pt-3 border-t border-neutral-200">
          <Skeleton className="h-5 w-32" />
        </div>
      ) : null}
    </div>
  );
}

export function SessionDetailPanel({
  session,
  isLoading,
  error,
  onRetry,
  traces,
  tracesLoading = false,
  tracesError = false,
  highlightMessageId,
  emptyText,
}: SessionDetailPanelProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = dateFnsLocale(i18n.language);

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i}>
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-20 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-6 gap-1.5">
        <p className="text-sm font-medium text-neutral-700">
          {t('admin.sessionDetail.loadErrorTitle')}
        </p>
        <p className="text-xs text-neutral-500 max-w-xs">
          {t('admin.sessionDetail.loadErrorBody')}
        </p>
        {onRetry && (
          <button onClick={onRetry} className="text-sm text-primary hover:underline">
            {t('actions.retry')}
          </button>
        )}
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex-1 flex items-center justify-center text-neutral-400 text-sm">
        {emptyText ?? t('admin.sessionDetail.selectPrompt')}
      </div>
    );
  }

  // Merge traces onto a turn by its seq (every trace row names the turn's seq).
  const tracesBySeq = new Map<number, SessionTraceResponse[]>();
  for (const trace of traces ?? []) {
    const list = tracesBySeq.get(trace.seq) ?? [];
    list.push(trace);
    tracesBySeq.set(trace.seq, list);
  }

  return (
    <ScrollArea className="flex-1">
      <div className="p-4 space-y-4">
        <div className="pb-3 border-b border-neutral-200">
          <h3 className="font-medium text-neutral-900">
            {session.session.title || t('admin.sessionDetail.untitled')}
          </h3>
          <p className="text-xs text-neutral-500">
            {t('admin.sessionDetail.messageCount', { count: session.messages.items.length })}
          </p>
          {tracesError && (
            <p className="text-xs text-red-600 mt-1">
              {t('admin.sessionDetail.toolTracesUnavailable')}
            </p>
          )}
        </div>

        {session.messages.items.length === 0 ? (
          <div className="text-center py-8 text-neutral-500 text-sm">
            {t('admin.sessionDetail.noMessages')}
          </div>
        ) : (
          session.messages.items.map((message) => (
            <MessageRow
              key={message.id}
              message={message}
              traces={tracesBySeq.get(message.seq) ?? []}
              tracesLoading={tracesLoading}
              isHighlighted={message.id === highlightMessageId}
              dateLocale={dateLocale}
            />
          ))
        )}
      </div>
    </ScrollArea>
  );
}
